import { faHeading } from "@fullscript/pro-solid-svg-icons";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createHeadingNode, $isHeadingNode } from "@lexical/rich-text";
import { $wrapNodes } from "@lexical/selection";
import { mergeRegister } from "@lexical/utils";
import { $createParagraphNode, $getSelection, $isRangeSelection } from "lexical";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";

import { ToolbarButton } from "../ToolbarButton";

const HeadingFormatButtons = () => {
  const { t } = useTranslation();

  const [editor] = useLexicalComposerContext();

  const [blockType, setBlockType] = useState("paragraph");

  // ensures toolbar buttons are in an active state when they should be by looking at the DOM element types in the lexical tree
  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if (elementDOM !== null) {
        const type = $isHeadingNode(element) ? element.getTag() : element.getType();
        setBlockType(type);
      }
    }
  }, [editor]);

  // looks/reads the current state of the editor, and calls updateToolbar when the RTE has edits
  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      })
    );
  }, [updateToolbar, editor]);

  const formatHeading = () => {
    editor.update(() => {
      const createNodeType = blockType === "h2" ? $createParagraphNode() : $createHeadingNode("h2");
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        $wrapNodes(selection, () => createNodeType);
      }
    });
  };

  return (
    <ToolbarButton
      icon={faHeading}
      isActive={blockType === "h2"}
      onClick={formatHeading}
      aria-label={t(l.aviary.richTextEditor.heading)}
    />
  );
};

export { HeadingFormatButtons };
